/**
 * Date-range resolution for the unified school print dialog — presets,
 * campus-clock anchoring, and the custom-range clamp. Pure; UTC date keys
 * (`YYYY-MM-DD`) throughout, matching how `serviceDate` is stored.
 *
 * Week semantics are `weekOf`'s (lib/school-orders/calendar): Mon–Fri, with
 * weekend anchors snapping FORWARD to the upcoming service week. This is the
 * deliberate standardization — the retired `printPresetRanges` snapped a
 * Sunday BACKWARD, so a worker prepping Sunday night for Monday printed the
 * week that had already ended.
 */

import { addDaysKey, mondayOf, weekOf } from '@/lib/school-orders/calendar'

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

export interface SchoolPrintRange {
  /** Inclusive `YYYY-MM-DD` bounds. */
  from: string
  to: string
  /** Every candidate day in the range (service-day filtering happens later). */
  days: string[]
}

/**
 * Longest custom range the dialog accepts, in calendar days. Fourteen covers
 * "print the next two service weeks" while staying far inside the
 * service-history endpoint's 60-day cap and keeping the document fan-out
 * (N records + N×mealtime sessions) bounded.
 */
export const SCHOOL_PRINT_MAX_RANGE_DAYS = 14

/**
 * Today's `YYYY-MM-DD` on the CAMPUS's clock. Presets must anchor here, not
 * on the browser: an admin printing from another timezone (or a laptop that
 * roamed) would otherwise get "Today" sheets for the wrong service day.
 * en-CA renders YYYY-MM-DD directly.
 */
export function campusTodayIso(timeZone: string, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now)
  } catch {
    // Unknown/garbled zone (bad provisioning) — browser-local beats crashing.
    return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)
  }
}

function rangeFromDays(days: string[]): SchoolPrintRange {
  return { from: days[0]!, to: days[days.length - 1]!, days }
}

/** The dialog's three fixed presets, anchored on the campus's today. */
export function schoolPrintPresets(anchorIso: string): {
  today: SchoolPrintRange
  thisWeek: SchoolPrintRange
  nextWeek: SchoolPrintRange
} {
  const thisWeekDays = weekOf(anchorIso)
  const nextWeekDays = weekOf(addDaysKey(mondayOf(anchorIso), 7))
  return {
    today: rangeFromDays([anchorIso]),
    thisWeek: rangeFromDays(thisWeekDays),
    nextWeek: rangeFromDays(nextWeekDays),
  }
}

/**
 * Validate + clamp a custom range: bounds must parse, be ascending, and span
 * at most `SCHOOL_PRINT_MAX_RANGE_DAYS` calendar days (clamped from `from`,
 * never silently reordered — a reversed range is a user mistake, not a
 * preference). Returns null when either bound is malformed.
 */
export function clampCustomRange(from: string, to: string): SchoolPrintRange | null {
  if (!ISO_DAY.test(from) || !ISO_DAY.test(to)) {
    return null
  }
  if (to < from) {
    return null
  }
  const days: string[] = []
  let cursor = from
  while (cursor <= to && days.length < SCHOOL_PRINT_MAX_RANGE_DAYS) {
    days.push(cursor)
    cursor = addDaysKey(cursor, 1)
  }
  return rangeFromDays(days)
}
